import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Text, Title } from "components/text";
import { Column } from "components/structure";
import { ErrorCard } from "components/errors";
import useCartaObjectStorage from "hooks/useCartaObjectStorage";
import { IProjectError } from "data/types";

export const Errors: FunctionComponent = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const cartaObjectStorage = useCartaObjectStorage();
  const [errors, setErrors] = useState<IProjectError[]>();
  const [error, setError] = useState<string>();

  const refreshErrors = useCallback(async () => {
    if (!cartaObjectStorage || !projectId) return;
    const response = await cartaObjectStorage.getPstErrors({ projectId });
    const sorted = [...response.errors].sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
    setErrors(sorted);
  }, [cartaObjectStorage, projectId]);

  useEffect(() => {
    (async () => {
      try {
        await refreshErrors();
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred.");
      }
    })();
  }, [refreshErrors]);

  const handleClear = useCallback(
    async (errorId: string) => {
      if (!cartaObjectStorage || !projectId) return;
      try {
        await cartaObjectStorage.clearPstError({ projectId }, errorId);
        setErrors((current) => current?.filter((entry) => entry.id !== errorId));
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred.");
      }
    },
    [cartaObjectStorage, projectId]
  );

  const handleRetry = useCallback(
    async (errorId: string) => {
      if (!cartaObjectStorage || !projectId) return;
      try {
        // The retry replaces the old error entry (cleared on success; replaced with a
        // fresh one on failure), so the list is re-fetched to pick up whatever changed.
        await cartaObjectStorage.retryPstError({ projectId }, errorId);
        await refreshErrors();
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred.");
      }
    },
    [cartaObjectStorage, projectId, refreshErrors]
  );

  if (!projectId) {
    return <Text color="error">No project selected.</Text>;
  }

  return (
    <Column style={{ gap: "1em" }}>
      <Title level={2} size="medium">
        Errors
      </Title>
      {error && <Text color="error">{error}</Text>}
      {!errors && !error && <Text>Loading...</Text>}
      {errors && errors.length === 0 && <Text>No errors for this project.</Text>}
      {errors && errors.length > 0 && (
        <Column style={{ gap: "0.5em" }}>
          {errors.map((entry) => (
            <ErrorCard key={entry.id} entry={entry} onClear={handleClear} onRetry={handleRetry} />
          ))}
        </Column>
      )}
    </Column>
  );
};
